/**
 * Google Identity Services (gratuito): botão "Entrar com Google" e autorização da Google Agenda.
 * Só funciona com GOOGLE_CLIENT_ID configurado na API (vem em /public/tenant).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: any;
  }
}

let loading: Promise<void> | null = null;

export function loadGoogle(): Promise<void> {
  if (window.google?.accounts) return Promise.resolve();
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error('Não foi possível carregar o Google. Verifique a internet.'));
      document.head.appendChild(s);
    });
  }
  return loading;
}

/** Desenha o botão oficial do Google; `onCredential` recebe o ID token. */
export async function renderGoogleButton(
  el: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
  dark: boolean,
) {
  await loadGoogle();
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (r: { credential: string }) => onCredential(r.credential),
  });
  window.google.accounts.id.renderButton(el, {
    theme: dark ? 'filled_black' : 'outline',
    size: 'large',
    shape: 'pill',
    text: 'signin_with',
    width: Math.min(360, el.clientWidth || 360),
    locale: 'pt-BR',
  });
}

/** Abre o consentimento da Google Agenda e devolve o código de autorização. */
export async function requestCalendarCode(clientId: string, email?: string): Promise<string> {
  await loadGoogle();
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email https://www.googleapis.com/auth/calendar.events.owned',
      ux_mode: 'popup',
      prompt: 'consent',
      login_hint: email,
      callback: (r: { code?: string; error?: string }) =>
        r.code ? resolve(r.code) : reject(new Error('Autorização cancelada.')),
      error_callback: () => reject(new Error('Autorização cancelada.')),
    });
    client.requestCode();
  });
}

/** Abre o consentimento do Google Drive do escritório (só as pastas criadas pela plataforma). */
export async function requestDriveCode(clientId: string, email?: string): Promise<string> {
  await loadGoogle();
  return new Promise((resolve, reject) => {
    const client = window.google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'openid email https://www.googleapis.com/auth/drive.file',
      ux_mode: 'popup',
      prompt: 'consent',
      login_hint: email,
      callback: (r: { code?: string; error?: string }) =>
        r.code ? resolve(r.code) : reject(new Error('Autorização cancelada.')),
      error_callback: () => reject(new Error('Autorização cancelada.')),
    });
    client.requestCode();
  });
}
